<template>
  <UiComponentWrapper class="page">
    <UiComponentContainer class="inner">
      <TemplateVirtualBackground
        :animated-images="data.page.heroAnimatedImages"
        :cards="virtualBackgroundCards"
        :categories="virtualBackgroundCategories"
        :current-page="1"
        :image1="data.page.heroImage1"
        :image2="data.page.heroImage2"
        :image3="data.page.heroImage3"
        :no-match-image="data.page.emptySearchImage"
        :pagination-root="localePath('virtual-background-library')"
        :primary-call-to-action="primaryCallToAction"
        :push-card="pushCard"
        :secondary-call-to-action="secondaryCallToAction"
      >
        <template #title>
          {{ data.page.heroTitle }}
        </template>
        <template #description>
          {{ data.page.heroText }}
        </template>
        <template #subtitle>
          {{ data.page.searchBarTitle }}
        </template>
        <template #placeholder>
          {{ data.page.searchBarPlaceholder }}
        </template>
        <template #all>
          {{ data.page.all }}
        </template>
        <template #more>
          {{ data.page.showMore }}
        </template>
        <template #no-match-title>
          {{ data.page.emptySearchTitle }}
        </template>
        <template #no-match-description>
          {{ data.page.emptySearchText }}
        </template>
      </TemplateVirtualBackground>
      <OrganismVirtualBackgroundInstallationBlock
        :cards="installationBlock.cards"
        :cta="installationBlock.cta"
      >
        <template #title>
          {{
            data.componentHowToAddVirtualBackground.howToTitle
          }}
        </template>
      </OrganismVirtualBackgroundInstallationBlock>
      <MoleculeCallToAction
        :call-to-action="{
          ...primaryCallToAction,
          label: 'Small section CTA',
        }"
        :pattern-desktop="data.componentGetStarted.backgroundImage"
        :pattern-mobile="data.componentGetStarted.backgroundImage"
      >
        <template #heading>
          {{ data.componentGetStarted.title }}
        </template>
        <template #paragraph>
          {{ data.componentGetStarted.paragraph }}
        </template>
      </MoleculeCallToAction>
      <UiComponentWrapper class="features">
        <UiComponentHeading :level="2">
          {{ data.page.featuresTitle }}
        </UiComponentHeading>
        <UiComponentWrapper>
          <MoleculeFeature
            v-for="{ id, icon, title, text } in data.page.featuresItems"
            :key="id"
            :direction="featureDirection"
            :feature="{ icon, description: text, title }"
          />
        </UiComponentWrapper>
      </UiComponentWrapper>
      <UiComponentWrapper
        v-if="data.page.faqItems && data.page.faqItems.length > 0"
        class="faq"
      >
        <UiComponentWrapper
          v-for="{ id, title, text } in data.page.faqItems"
          :key="id"
          class="items"
        >
          <UiComponentHeading :level="2">
            {{ title }}
          </UiComponentHeading>
          <OrganismRichText
            :content="[{ __typename: 'ContentTextHtmlRecord', text }]"
          />
        </UiComponentWrapper>
      </UiComponentWrapper>
      <UiComponentWrapper
        v-if="platformLinks.length > 0"
        class="explore"
      >
        <UiComponentHeading :level="2">
          {{ data.page.exploreTitle }}
        </UiComponentHeading>
        <UiComponentWrapper>
          <AtomButton
            v-for="{ id, slug, platformShortName } in platformLinks"
            :key="id"
            :to="
              localePath({
                name: 'virtual-background-library-platform-slug',
                params: {
                  slug,
                },
              })
            "
            type="text"
          >
            {{ platformShortName }}
          </AtomButton>
        </UiComponentWrapper>
        <AtomButton
          v-if="platformLinks.length > 7"
          to="#"
          type="text"
          class="action"
          @click="showAllPlatformLinks = !showAllPlatformLinks"
        >
          {{ showPlatformButtonWording }}
        </AtomButton>
      </UiComponentWrapper>
    </UiComponentContainer>

    <!-- Section > Related Posts -->
    <OrganismHighlightedPosts
      v-if="data.page.relatedPosts.length >= 4 || data.componentRelatedPosts.items.length > 0"
      class="our-guides"
      :items="
        data.page.relatedPosts.length >= 4
          ? data.page.relatedPosts
          : data.componentRelatedPosts.items
      "
      :minutes-read="data.componentGlobal.minRead"
    >
      {{ data.page.relatedPostsTitle || data.componentRelatedPosts.title }}
    </OrganismHighlightedPosts>

    <!-- Section > Our Guides -->
    <OrganismHighlightedGuides
      v-if="data.allPillarPages.length > 0 || data.page.ourGuides.length > 0"
      class="our-guides"
      :items="
        data.page.ourGuides.length > 0
          ? data.page.ourGuides
          : data.allPillarPages
      "
    >
      {{ data.page.ourGuidesTitle || data.componentOurGuides.title }}
    </OrganismHighlightedGuides>
  </UiComponentWrapper>
</template>

<script setup>
// Layout
definePageMeta({
  layout: 'default',
})

// i18n
defineI18nRoute({
  locales: ['es', 'de', 'en', 'fr'],
  paths: {
    es: '/biblioteca-fondos-virtuales',
    de: '/virtuelle-hintergrund-bibliothek',
    en: '/virtual-background-library',
    fr: '/arriere-plan-virtuel',
  },
})

// Composables
const { page } = useAnalyticsJS()
const { setAvailableLanguages } = useAvailableLanguages()
const { updateListItems } = useBreadcrumb()
const { state } = useConditionalPreviewMode()
const { locale } = useI18n()
const { isLogged } = useIsLoggedIn()
const localePath = useLocalePath()
const getRouteBaseName = useRouteBaseName()
const { public: publicRuntimeConfig } = useRuntimeConfig()
const { width: screenWidth } = useScreenSize()

// Async Data
const { data } = await useAsyncData(
  `page-virtual-background-library-${locale.value}`,
  async () => {
    return Promise.all([
      $fetch(`/api/content/pages/virtual-background-index/${locale.value}`, { query: { preview: state.preview } }),
      $fetch(`/api/content/pages/virtual-background-list/${locale.value}`, { query: { preview: state.preview } }),
    ])
  },
  {
    transform: ([{ allCategories, allPillarPages, allPlatforms, componentGetStarted, componentGlobal, componentHowToAddVirtualBackground, componentOurGuides, componentRelatedPosts, home, page }, allBackgrounds]) => {
      return {
        allBackgrounds,
        allCategories,
        allPillarPages,
        allPlatforms,
        componentGetStarted,
        componentGlobal,
        componentHowToAddVirtualBackground,
        componentOurGuides,
        componentRelatedPosts,
        home,
        page,
      }
    },
  },
)

// Meta
const head = useLocaleHead({
  identifierAttribute: 'id',
  addSeoAttributes: true,
})

useSeoMeta({
  title: data.value.page.seoTitle,
  description: data.value.page.seoDescription,
  ogTitle: data.value.page.seoTitle,
  ogDescription: data.value.page.seoDescription,
  twitterTitle: data.value.page.seoTitle,
  twitterDescription: data.value.page.seoDescription,
})

useHead({
  link: head.value.link,
  meta: head.value.meta,
  script: [
    {
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        'itemListElement': [
          {
            '@type': 'ListItem',
            'position': 1,
            'name': data.value.home.trackingBreadcrumbTitle,
            'item': `${publicRuntimeConfig.baseUrl}${localePath('index')}`,
          },
          {
            '@type': 'ListItem',
            'position': 2,
            'name': data.value.page.trackingBreadcrumbTitle,
          },
        ],
      }),
      type: 'application/ld+json',
    },
    {
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        'mainEntity': data.value.page.faqItems.map(({ title, text }) => ({
          '@type': 'Question',
          'name': title,
          'acceptedAnswer': {
            '@type': 'Answer',
            text,
          },
        })),
      }),
      type: 'application/ld+json',
    },
  ],
})

// Refs
const showAllPlatformLinks = ref(false)

// Computed Properties
const featureDirection = computed(() => {
  return screenWidth.value >= 768 && screenWidth.value < 1328 ? 'row' : 'column'
})

const installationBlock = computed(() => {
  return {
    cards: data.value.componentHowToAddVirtualBackground.howToSteps.map(
      ({ id, image, number, text }) => ({
        id,
        image,
        paragraph: text,
        step: number,
      }),
    ),
    cta: {
      action: '',
      to: data.value.componentHowToAddVirtualBackground.ctaLink,
      slot: data.value.componentHowToAddVirtualBackground.ctaTitle,
    },
  }
})

const platformLinks = computed(() => {
  const numberOfLinks = showAllPlatformLinks.value
    ? data.value.allPlatforms.length
    : 6
  return data.value.allPlatforms.slice(
    0,
    numberOfLinks,
  )
})

const primaryCallToAction = computed(() => {
  return isLogged.value
    ? {
        action: 'Upgrade clicked',
        slot: data.value.componentGlobal.ctaUpgradeNow,
        to: `https://${publicRuntimeConfig.appBaseUrl}/#/settings?page=settings&tab=billing`,
      }
    : {
        action: 'Signup clicked',
        alternativeSlot: data.value.componentGlobal.ctaGetStartedForFree,
        slot: data.value.componentGlobal.ctaTryForFree,
        to: `https://${publicRuntimeConfig.appBaseUrl}/#/signup`,
      }
})

const pushCard = computed(() => {
  return {
    pattern: data.value.page.pushBackgroundImage,
    primaryCallToAction: primaryCallToAction.value,
    secondaryCallToAction: secondaryCallToAction.value,
    text: data.value.page.pushText,
    title: data.value.page.pushTitle,
    type: 'push-card',
  }
})

const representedCategories = computed(() => {
  return [
    ...new Set(
      virtualBackgroundCards.value.map(({ category: { id } }) => id),
    ),
  ]
})

const secondaryCallToAction = computed(() => {
  return {
    to: '#form-demo',
    action: 'Demo clicked',
    slot: data.value.componentGlobal.ctaGetDemo,
  }
})

const showPlatformButtonWording = computed(() => {
  return showAllPlatformLinks.value
    ? data.value.page.showLess
    : data.value.page.showMore
})

const virtualBackgroundCards = computed(() => {
  return data.value.allBackgrounds
    .filter(({ slug }) => !!slug)
    .map(
      ({
        id,
        _firstPublishedAt,
        backgroundImageForWebsite,
        backgroundTitle,
        category,
        createdAt,
        isDaylightBackground,
        searchLexicalField,
        slug,
      }) => ({
        id,
        _firstPublishedAt,
        category,
        createdAt,
        image: backgroundImageForWebsite,
        isDaylightBackground: !!isDaylightBackground,
        searchLexicalField,
        slug,
        title: backgroundTitle,
        type: 'background-card',
      }),
    )
})

const virtualBackgroundCategories = computed(() => {
  return data.value.allCategories
    .filter(({ id }) => representedCategories.value.includes(id))
    .map(({ id, categoryShortName, slug }) => ({
      id,
      name: categoryShortName,
      slug,
    }))
})

// Lifecycle Hooks
onMounted(() => {
  page(data.value.page.trackName || data.value.page.trackNameFallback)
  setAvailableLanguages(['es', 'de', 'en', 'fr'])
  updateListItems([
    {
      item: localePath('index'),
      name: data.value.home.trackingBreadcrumbTitle,
    },
    {
      item: localePath({ name: getRouteBaseName() }),
      name: data.value.page.trackingBreadcrumbTitle,
    },
  ])
})
</script>

<style lang="postcss" scoped>
.page {
  @apply relative flex-col items-center;
  overflow-y: visible;
  overflow-x: clip;

  .inner {
    @apply flex-col;
  }

  .virtual-background-installation-block {
    @apply mt-34;

    :deep(h2) {
      @screen md {
        @apply text-5xl leading-16;
      }

      @screen xl {
        @apply text-center;
        max-width: 840px;
      }

      strong {
        @apply text-winter-green-900 !important;
      }
    }

    :deep(.installation-card-step) {
      @screen md {
        width: 720px;
      }

      @screen xl {
        width: 400px;
      }
    }
  }

  .small-call-to-action {
    @apply mt-34;

    @screen mdminus {
      @apply mt-20;
    }

    @screen mdplus {
      @apply mt-34;
    }

    @screen xl {
      @apply self-center;
      max-width: 792px;
    }
  }

  .features {
    @apply mt-34 flex-col;

    @screen mdminus {
      @apply mt-20;
    }

    @screen mdplus {
      @apply mt-34;
    }

    @screen xl {
      @apply items-center;
    }

    h2 {
      @apply font-normal !important;

      @screen md {
        @apply text-5xl leading-16;
      }

      @screen xl {
        @apply text-center;
        max-width: 840px;
      }

      :deep(strong),
      :deep(b) {
        @apply font-normal text-winter-green-700 !important;
      }
    }

    .wrapper {
      @apply mt-20 flex-col;

      @screen xl {
        @apply flex-row;
      }

      :deep(.feature) {
        @apply mt-10;

        &:first-child {
          @apply mt-0;
        }

        @screen xl {
          @apply mt-0 ml-10 items-center text-center;

          &:first-child {
            @apply ml-0;
          }
        }

        h4 {
          @apply font-medium;

          @screen md {
            @apply text-2xl leading-8;
          }
        }

        &.row {
          @screen md {
            @apply flex-row;
          }

          .body {
            @screen md {
              @apply mt-0 ml-6;
            }
          }
        }
      }
    }
  }

  .faq {
    @apply flex-col items-center mt-34;

    @screen mdminus {
      @apply mt-20;
    }

    @screen mdplus {
      @apply mt-34;
    }

    .items {
      @apply flex flex-col mt-12;

      @screen md {
        @apply mt-20;
      }

      @screen xl {
        max-width: 1060px;
      }

      &:first-child {
        @apply mt-0;
      }

      .h2 {
        @apply font-medium text-2xl leading-8;

        @screen mdminus {
          font-size: 28px;
          line-height: 34px;
        }

        @screen mdplus {
          @apply text-3xl leading-10;
        }
      }

      :deep(.rich-text) {
        .text-record {
          @apply mt-3;

          p {
            @apply text-grey-blue-600;
          }

          li {
            @apply text-grey-blue-600;
          }
        }
      }
    }
  }

  .explore {
    @apply mt-14 flex-col items-center;

    @screen mdminus {
      @apply mt-26;
    }

    @screen mdplus {
      @apply mt-46;
    }

    h2 {
      @apply font-normal text-center !important;

      @screen md {
        @apply text-5xl leading-16;
      }

      @screen xl {
        max-width: 1000px;
      }
    }

    .wrapper {
      @apply flex-wrap justify-center items-center gap-6 max-w-[840px] mt-12;

      @screen md {
        @apply gap-y-4;
      }

      .button {
        @apply h-6 text-livestorm-blue-700 font-bold text-sm leading-6;
      }
    }

    .button.action {
      @apply font-bold;

      @screen md {
        @apply mt-2;
      }

      @screen xl {
        @apply mt-4;
      }
    }
  }
}
</style>
